import { CHART_INK, CHART_SERIES, CHART_SEMANTIC } from '../constants/chartPalette';
import { STAT_TONES } from '../constants/statTones';

/**
 * `color_token` — the colour the **backend** chose (CT-5641).
 *
 * Eleven of the twenty-nine types return one. It is not decoration: it is the
 * answer to a threshold the author configured, evaluated server-side against
 * the value, the delta or the target percentage — whichever
 * `display.thresholds_applies_to` named. The widget's job is to paint it, not
 * to decide it.
 *
 * That is the whole reason it travels. A front end that recomputed the colour
 * would need the thresholds, the ratio they apply to, and the same rounding —
 * and would disagree with the backend the first time any of the three drifted.
 *
 * ## Two vocabularies, one map
 *
 * `NEUTRAL | INFO | SUCCESS | WARNING | DANGER` are **states**, and they map
 * onto the tone vocabulary the non-chart widgets already share, so a DANGER
 * KPI is the same red as a critical alert row.
 *
 * `CATEGORICAL_1..8` are **identities** — this series is not that series — and
 * map onto the chart palette, so a token'd series keeps the colour it would
 * have had by position.
 *
 * An absent or unknown token resolves to neutral rather than to nothing: a
 * widget that paints no colour at all reads as broken, where a neutral one
 * reads as "no threshold applied", which is the truth.
 */

/** State token → the shared tone name (`statTones`). */
const TONE_BY_TOKEN = Object.freeze({
  NEUTRAL: 'neutral',
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  DANGER: 'critical',
});

/**
 * State token → a CSS colour, for the drawings that stroke and fill.
 *
 * `NEUTRAL` is the muted ink the rest of the chrome uses, not the gridline
 * colour: a NEUTRAL series is still a series the reader has to be able to
 * follow. The contract's own line-chart example sends `NEUTRAL` for its
 * "Others" series, carrying a third of the volume — drawn at gridline weight it
 * is indistinguishable from the grid, which is the same as not drawing it.
 */
const COLOUR_BY_TOKEN = Object.freeze({
  NEUTRAL: CHART_INK.muted,
  INFO: CHART_SERIES[0],
  SUCCESS: CHART_SEMANTIC.ok,
  WARNING: CHART_SEMANTIC.warn,
  DANGER: CHART_SEMANTIC.bad,
});

const categoricalIndex = (token) => {
  const match = /^CATEGORICAL_(\d+)$/.exec(String(token ?? ''));
  return match ? (Number(match[1]) - 1) % CHART_SERIES.length : null;
};

/**
 * The tone name for a token, for the widgets that speak tones (badges, rows,
 * icon highlights).
 *
 * A categorical token has no tone — it names an identity, not a state — so it
 * reads as neutral here and takes its colour from `colourForToken` instead.
 *
 * @param {string|null|undefined} token
 * @returns {'neutral'|'info'|'success'|'warning'|'critical'}
 */
export const toneForToken = (token) => TONE_BY_TOKEN[token] ?? 'neutral';

/**
 * The CSS colour for a token, for the widgets that stroke and fill.
 *
 * @param {string|null|undefined} token
 * @param {string} [fallback] Used when there is no token — typically the
 *   colour the series would have had by its position.
 * @returns {string}
 */
export const colourForToken = (token, fallback = COLOUR_BY_TOKEN.NEUTRAL) => {
  const index = categoricalIndex(token);
  if (index !== null) return CHART_SERIES[index];
  return COLOUR_BY_TOKEN[token] ?? fallback;
};

/** The `Badge` variant for a token, so a coloured value and a badge agree. */
export const badgeForToken = (token) => STAT_TONES[toneForToken(token)].badge;

export default colourForToken;
